import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MessageSquare, MessageSquarePlus, Pencil, Trash2 } from "lucide-react";
import { format, parseISO } from "date-fns";

interface WorkoutLogDetailDialogProps {
  workoutLogId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SetLogRow {
  id: string;
  exercise_id: string;
  set_number: number;
  weight: number | null;
  weight_unit: string | null;
  reps_completed: number | null;
  rpe: number | null;
  is_warmup: boolean;
  notes: string | null;
  exercise: { id: string; name: string } | null;
}

interface SetLogComment {
  id: string;
  set_log_id: string;
  author_id: string;
  body: string;
  created_at: string;
  updated_at: string;
}

interface ExerciseGroup {
  exerciseId: string;
  name: string;
  sets: SetLogRow[];
}

const MAX_BODY = 1000;

export function WorkoutLogDetailDialog({
  workoutLogId,
  open,
  onOpenChange,
}: WorkoutLogDetailDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [setLogs, setSetLogs] = useState<SetLogRow[]>([]);
  const [comments, setComments] = useState<SetLogComment[]>([]);
  const [workoutMeta, setWorkoutMeta] = useState<{
    name: string;
    startedAt: string;
  } | null>(null);

  const loadData = useCallback(async () => {
    if (!workoutLogId) return;
    setLoading(true);
    try {
      const { data: logData, error: logErr } = await supabase
        .from("workout_logs")
        .select("id, started_at, template:workout_templates(name)")
        .eq("id", workoutLogId)
        .single();
      if (logErr) throw logErr;

      const { data: setData, error: setErr } = await supabase
        .from("set_logs")
        .select(
          "id, exercise_id, set_number, weight, weight_unit, reps_completed, rpe, is_warmup, notes, exercise:exercises(id, name)"
        )
        .eq("workout_log_id", workoutLogId)
        .order("created_at", { ascending: true });
      if (setErr) throw setErr;

      const rows = (setData as unknown as SetLogRow[]) || [];
      const setIds = rows.map((r) => r.id);

      let commentRows: SetLogComment[] = [];
      if (setIds.length > 0) {
        const { data: cData, error: cErr } = await (supabase as any)
          .from("set_log_comments")
          .select("id, set_log_id, author_id, body, created_at, updated_at")
          .in("set_log_id", setIds)
          .order("created_at", { ascending: true });
        if (cErr) throw cErr;
        commentRows = (cData as SetLogComment[]) || [];
      }

      setWorkoutMeta({
        name:
          (logData as any)?.template?.name ||
          (Array.isArray((logData as any)?.template)
            ? (logData as any).template[0]?.name
            : null) ||
          "Workout",
        startedAt: (logData as any).started_at,
      });
      setSetLogs(rows);
      setComments(commentRows);
    } catch (err: any) {
      console.error("Failed to load workout detail:", err);
      toast.error(err.message || "Failed to load workout detail");
    } finally {
      setLoading(false);
    }
  }, [workoutLogId]);

  useEffect(() => {
    if (open && workoutLogId) {
      loadData();
    } else {
      setSetLogs([]);
      setComments([]);
      setWorkoutMeta(null);
    }
  }, [open, workoutLogId, loadData]);

  const handleAddComment = async (setLogId: string, body: string) => {
    if (!user) {
      toast.error("Not signed in");
      return false;
    }
    const trimmed = body.trim();
    if (!trimmed) return false;
    try {
      const { data, error } = await (supabase as any)
        .from("set_log_comments")
        .insert({
          set_log_id: setLogId,
          author_id: user.id,
          body: trimmed,
        })
        .select("id, set_log_id, author_id, body, created_at, updated_at")
        .single();
      if (error) throw error;
      setComments((prev) => [...prev, data as SetLogComment]);
      toast.success("Comment added");
      return true;
    } catch (err: any) {
      console.error("Failed to add comment:", err);
      toast.error(err.message || "Failed to add comment");
      return false;
    }
  };

  const handleUpdateComment = async (id: string, body: string) => {
    const trimmed = body.trim();
    if (!trimmed) return false;
    try {
      const { data, error } = await (supabase as any)
        .from("set_log_comments")
        .update({ body: trimmed, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select("id, set_log_id, author_id, body, created_at, updated_at")
        .single();
      if (error) throw error;
      setComments((prev) =>
        prev.map((c) => (c.id === id ? (data as SetLogComment) : c))
      );
      toast.success("Comment updated");
      return true;
    } catch (err: any) {
      console.error("Failed to update comment:", err);
      toast.error(err.message || "Failed to update comment");
      return false;
    }
  };

  const handleDeleteComment = async (id: string) => {
    try {
      const { error } = await (supabase as any)
        .from("set_log_comments")
        .delete()
        .eq("id", id);
      if (error) throw error;
      setComments((prev) => prev.filter((c) => c.id !== id));
      toast.success("Comment deleted");
    } catch (err: any) {
      console.error("Failed to delete comment:", err);
      toast.error(err.message || "Failed to delete comment");
    }
  };

  // Group sets by exercise (preserving insertion order)
  const groups: ExerciseGroup[] = [];
  const groupIndex = new Map<string, number>();
  for (const s of setLogs) {
    const key = s.exercise_id;
    if (!groupIndex.has(key)) {
      groupIndex.set(key, groups.length);
      groups.push({
        exerciseId: key,
        name: s.exercise?.name || "Exercise",
        sets: [],
      });
    }
    groups[groupIndex.get(key)!].sets.push(s);
  }

  const commentsBySetId = comments.reduce<Record<string, SetLogComment[]>>(
    (acc, c) => {
      (acc[c.set_log_id] ||= []).push(c);
      return acc;
    },
    {}
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{workoutMeta?.name || "Workout"}</DialogTitle>
          <DialogDescription>
            {workoutMeta?.startedAt
              ? format(parseISO(workoutMeta.startedAt), "EEEE, MMM d, yyyy h:mm a")
              : "Loading…"}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : groups.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground">
            No sets logged for this workout.
          </p>
        ) : (
          <div className="space-y-6">
            {groups.map((group) => (
              <div key={group.exerciseId} className="space-y-2">
                <h4 className="font-semibold text-sm">{group.name}</h4>
                <div className="space-y-1">
                  {group.sets.map((s) => {
                    const setComments = commentsBySetId[s.id] || [];
                    return (
                      <div
                        key={s.id}
                        className="rounded-md border bg-muted/20 p-3 space-y-2"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 text-sm">
                            <span className="font-medium w-10">
                              {s.is_warmup ? "WU" : `Set ${s.set_number}`}
                            </span>
                            <span>
                              {s.weight ?? 0} {s.weight_unit || "lbs"} ×{" "}
                              {s.reps_completed ?? 0}
                            </span>
                            {s.rpe && (
                              <Badge variant="outline" className="text-xs">
                                RPE {s.rpe}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {setComments.length > 0 && (
                              <Badge variant="secondary" className="gap-1">
                                <MessageSquare className="h-3 w-3" />
                                {setComments.length}
                              </Badge>
                            )}
                            <AddCommentPopover
                              onSubmit={(body) =>
                                handleAddComment(s.id, body)
                              }
                            />
                          </div>
                        </div>

                        {setComments.length > 0 && (
                          <div className="space-y-1.5 pt-1 border-t border-border/50">
                            {setComments.map((c) => (
                              <CommentRow
                                key={c.id}
                                comment={c}
                                isAuthor={user?.id === c.author_id}
                                onUpdate={(body) =>
                                  handleUpdateComment(c.id, body)
                                }
                                onDelete={() => handleDeleteComment(c.id)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// AddCommentPopover
// ---------------------------------------------------------------------------
function AddCommentPopover({
  onSubmit,
}: {
  onSubmit: (body: string) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setBody("");
    setSubmitting(false);
  };

  const handleSave = async () => {
    setSubmitting(true);
    const ok = await onSubmit(body);
    setSubmitting(false);
    if (ok) {
      reset();
      setOpen(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7" title="Add feedback">
          <MessageSquarePlus className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-2" align="end">
        <p className="text-sm font-medium">Inline feedback</p>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
          placeholder="e.g. good depth on rep 3, watch knee track…"
          rows={3}
          autoFocus
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{body.length}/{MAX_BODY}</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                reset();
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={submitting || body.trim().length === 0}
            >
              {submitting ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// CommentRow
// ---------------------------------------------------------------------------
function CommentRow({
  comment,
  isAuthor,
  onUpdate,
  onDelete,
}: {
  comment: SetLogComment;
  isAuthor: boolean;
  onUpdate: (body: string) => Promise<boolean>;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const ok = await onUpdate(draft);
    setSaving(false);
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <div className="space-y-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_BODY))}
          rows={3}
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setDraft(comment.body);
              setEditing(false);
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || draft.trim().length === 0}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-2 text-sm">
      <p className="flex-1 whitespace-pre-wrap text-foreground/90">
        {comment.body}
      </p>
      {isAuthor && (
        <div className="flex shrink-0 gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setEditing(true)}
            title="Edit"
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={onDelete}
            title="Delete"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default WorkoutLogDetailDialog;
